/**
 * Company Type Dropdown
 * 
 * Handles Select2 initialization and integration with membership filter system
 * for the company type dropdown shortcode
 *
 * @package bricks-arival
 */

(function() {
	'use strict';

	/**
	 * Company Type Dropdown Handler
	 */
	const CompanyTypeDropdown = {
		/**
		 * Initialize all company type dropdowns on the page
		 */
		init: function() {
			// Wait for DOM to be ready
			if (document.readyState === 'loading') {
				document.addEventListener('DOMContentLoaded', () => {
					this.setupDropdowns();
				});
			} else {
				// DOM already ready
				this.setupDropdowns();
			}
		},

		/**
		 * Setup all dropdowns with Select2 and event handlers
		 */
		setupDropdowns: function() {
			const dropdowns = document.querySelectorAll('.company-type-dropdown.select2-select');
			
			if (dropdowns.length === 0) {
				return;
			}

			// Check if Select2 is available
			if (typeof jQuery === 'undefined' || typeof jQuery.fn.select2 === 'undefined') {
				// Select2 not loaded yet, try again with exponential backoff. Stop
				// after 10 seconds (like the MutationObserver below), so a page
				// where it never loads doesn't keep polling.
				this._retryUntil = this._retryUntil || Date.now() + 10000;
				if (Date.now() < this._retryUntil) {
					const retryDelay = this._retryDelay || 100;
					this._retryDelay = Math.min(retryDelay * 1.5, 1000); // Max 1 second
					setTimeout(() => this.setupDropdowns(), retryDelay);
				}
				return;
			}

			// Reset retry state on success
			this._retryDelay = 100;
			this._retryUntil = 0;

			const $ = jQuery;

			dropdowns.forEach((dropdown) => {
				const $dropdown = $(dropdown);
				const wrapper = dropdown.closest('.company-type-dropdown-wrapper');

				// Hide dropdown wrapper initially (will fade in after Select2 is applied)
				if (wrapper && !wrapper.classList.contains('select2-initialized')) {
					wrapper.style.opacity = '0';
					wrapper.style.transition = 'opacity 0.3s ease-in-out';
				}

				// Destroy existing Select2 instance if present
				if ($dropdown.hasClass('select2-hidden-accessible')) {
					$dropdown.select2('destroy');
				}

				// Initialize Select2
				$dropdown.select2({
					placeholder: function() {
						// Get placeholder from first option
						const firstOption = dropdown.querySelector('option[value=""]');
						return firstOption ? firstOption.textContent : 'Select your company type...';
					}(),
					allowClear: true,
					width: '100%',
					minimumResultsForSearch: 0, // Always show search
					dropdownCssClass: 'company-type-dropdown-select2',
					language: {
						noResults: function() {
							return 'No company types found';
						}
					}
				});

				// Handle select and clear events (more reliable than native change event)
				$dropdown.on('select2:select select2:clear', (e) => {
					this.handleDropdownChange(e, dropdown);
				});

				// Add custom styling class
				$dropdown.next('.select2-container').addClass('company-type-dropdown-container');

				// Mark as initialized and fade in the wrapper
				if (wrapper) {
					wrapper.classList.add('select2-initialized');
					// Use requestAnimationFrame to ensure transition happens
					requestAnimationFrame(() => {
						requestAnimationFrame(() => {
							wrapper.style.opacity = '1';
						});
					});
				}

				// Restore selection AFTER dropdown is visible
				// This ensures filtering happens after the fade-in animation
				setTimeout(() => {
					this.restoreSelectionFromStorage(dropdown, $dropdown);
				}, 350); // Wait for fade-in (300ms) + small buffer
			});
		},

		/**
		 * Restore selection from localStorage
		 * First tries to restore the exact dropdown value, then falls back to operator type matching
		 * 
		 * @param {HTMLElement} dropdown The select element
		 * @param {jQuery} $dropdown jQuery object for the dropdown
		 */
		restoreSelectionFromStorage: function(dropdown, $dropdown) {
			try {
				// Get expiration days from membership filter system if available
				let expirationDays = 30;
				if (window.ArivalMembershipFilter && window.ArivalMembershipFilter.config) {
					expirationDays = window.ArivalMembershipFilter.config.expirationDays || expirationDays;
				} else if (window.arivalMembershipFilter) {
					expirationDays = window.arivalMembershipFilter.expirationDays || expirationDays;
				}

				// Get the specific dropdown selection
				const dropdownSelection = this.getDropdownSelection(expirationDays);
				
				let matchingOption = null;
				let operatorType = null;

				if (dropdownSelection && dropdownSelection.dropdownValue) {
					// Try to find exact match by dropdown value first
					matchingOption = dropdown.querySelector(`option[value="${CSS.escape(dropdownSelection.dropdownValue)}"]`);
					
					if (matchingOption) {
						// Found exact match - use its operator type
						operatorType = matchingOption.getAttribute('data-operator-type') || dropdownSelection.operatorType;
					} else if (dropdownSelection.operatorType) {
						// Exact match not found - fall back to first option with matching operator type
						// This handles cases where options have changed since last visit
						const options = dropdown.querySelectorAll('option[data-operator-type]');
						for (let option of options) {
							const optionType = option.getAttribute('data-operator-type');
							if (optionType === dropdownSelection.operatorType) {
								matchingOption = option;
								operatorType = optionType;
								break;
							}
						}
					}
				}

				// If found, select it and trigger filter
				if (matchingOption && operatorType) {
					// Mark as restoring to prevent change event from triggering filter
					dropdown.setAttribute('data-restoring', 'true');
					
					// Set value and update Select2 display
					$dropdown.val(matchingOption.value);
					$dropdown.trigger('change.select2');
					
					// Apply filter using event-based approach
					this.applyFilterOnRestore(operatorType, dropdown);
				}
			} catch (e) {
				// localStorage not available or error reading
				console.warn('Company Type Dropdown: Could not restore from localStorage', e);
			}
		},

		/**
		 * Apply filter when restoring from localStorage
		 * Uses event-based approach for more reliable initialization detection
		 * 
		 * @param {string} operatorType The operator type to filter by
		 * @param {HTMLElement} dropdown The dropdown element
		 */
		applyFilterOnRestore: function(operatorType, dropdown) {
			// Function to apply the filter
			const applyFilter = () => {
				if (window.ArivalMembershipFilter && window.ArivalMembershipFilter.isInitialized) {
					// System is initialized - check if filter matches
					if (window.ArivalMembershipFilter.currentFilter !== operatorType) {
						// Filter doesn't match, apply it
						window.ArivalMembershipFilter.handleFilterChange(operatorType);
					}
					// Remove restoring flag
					setTimeout(() => {
						dropdown.removeAttribute('data-restoring');
					}, 100);
					return true;
				}
				return false;
			};

			// Try to apply immediately if system is ready
			if (applyFilter()) {
				return;
			}

			// If not ready, listen for initialization event
			const handleFilterReady = (e) => {
				applyFilter();
				document.removeEventListener('arivalMembershipFilterReady', handleFilterReady);
			};

			document.addEventListener('arivalMembershipFilterReady', handleFilterReady);

			// Fallback: Check periodically with a reasonable timeout
			// This handles cases where the event system isn't available or event is missed
			let attempts = 0;
			const maxAttempts = 40; // 2 seconds max (40 * 50ms)
			const checkInterval = setInterval(() => {
				attempts++;
				if (applyFilter() || attempts >= maxAttempts) {
					clearInterval(checkInterval);
					document.removeEventListener('arivalMembershipFilterReady', handleFilterReady);
					if (attempts >= maxAttempts) {
						// Cleanup on timeout
						dropdown.removeAttribute('data-restoring');
					}
				}
			}, 50);
		},

		/**
		 * Handle dropdown change event
		 * Triggers membership filter system
		 * 
		 * @param {Event} e jQuery event object
		 * @param {HTMLElement} dropdown The select element
		 */
		handleDropdownChange: function(e, dropdown) {
			// Check if this is a restore operation (skip filter trigger)
			const isRestoring = dropdown.hasAttribute('data-restoring');

			const selectedValue = dropdown.value;
			const selectedOption = dropdown.options[dropdown.selectedIndex];

			// If cleared (empty value), reset filter to placeholder state and clear dropdown selection storage
			if (!selectedValue || selectedValue === '') {
				if (!isRestoring) {
					if (window.ArivalMembershipFilter && typeof window.ArivalMembershipFilter.handleFilterDeactivate === 'function') {
						window.ArivalMembershipFilter.handleFilterDeactivate();
					} else if (window.ArivalMembershipFilter && typeof window.ArivalMembershipFilter.showPlaceholderState === 'function') {
						// Fallback: directly show placeholder state
						window.ArivalMembershipFilter.showPlaceholderState(true);
					}
					// Clear the specific dropdown selection from storage
					this.clearDropdownSelection();
				}
				return;
			}

			// Get operator type from data attribute or value
			const operatorType = selectedOption ? selectedOption.getAttribute('data-operator-type') || selectedValue : selectedValue;

			// Save the specific dropdown selection (separate from filter classification)
			if (!isRestoring) {
				this.saveDropdownSelection(selectedValue, operatorType);
			}

			// Trigger membership filter (unless this is a restore operation)
			// During restore, the membership filter system will handle the state on its own
			if (!isRestoring) {
				if (window.ArivalMembershipFilter && typeof window.ArivalMembershipFilter.handleFilterChange === 'function') {
					window.ArivalMembershipFilter.handleFilterChange(operatorType);
				} else {
					// Fallback: try to trigger via button click if filter system not loaded
					console.warn('Company Type Dropdown: Membership filter system not available');
				}
			}
		},

		/**
		 * Set a cookie
		 * @param {string} name Cookie name
		 * @param {string} value Cookie value
		 * @param {number} days Expiry in days (negative to delete)
		 */
		setCookie: function(name, value, days) {
			const date = new Date();
			date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
			const expires = 'expires=' + date.toUTCString();
			const secure = location.protocol === 'https:' ? ';Secure' : '';
			document.cookie = name + '=' + encodeURIComponent(value) + ';' + expires + ';path=/;SameSite=Lax' + secure;
		},

		/**
		 * Save the specific dropdown selection to localStorage
		 * This is separate from the filter classification and allows exact restoration
		 * 
		 * @param {string} dropdownValue The exact value of the selected option
		 * @param {string} operatorType The operator classification (for backward compatibility)
		 */
		saveDropdownSelection: function(dropdownValue, operatorType) {
			try {
				const storageData = {
					dropdownValue: dropdownValue,
					operatorType: operatorType,
					timestamp: Date.now()
				};
				localStorage.setItem('arival_company_type_dropdown_selection', JSON.stringify(storageData));
			} catch (e) {
				console.warn('Company Type Dropdown: Could not save selection to localStorage', e);
			}
			// Also set cookie for server-side PHP access at checkout
			const cookieValue = (operatorType === 'non-operator') ? 'non-op' : 'operator';
			this.setCookie('arival_operator_type', cookieValue, 30);
		},

		/**
		 * Get the saved dropdown selection from localStorage
		 * 
		 * @param {number} expirationDays Number of days before selection expires
		 * @return {object|null} Object with dropdownValue and operatorType, or null if not found/expired
		 */
		getDropdownSelection: function(expirationDays) {
			try {
				const stored = localStorage.getItem('arival_company_type_dropdown_selection');
				if (!stored) {
					return null;
				}

				const storageData = JSON.parse(stored);
				
				// Check expiration
				if (storageData.timestamp) {
					const currentTime = Date.now();
					const expirationMs = expirationDays * 24 * 60 * 60 * 1000;
					const timeDiff = currentTime - storageData.timestamp;
					
					if (timeDiff > expirationMs) {
						// Expired - clear and return null
						this.clearDropdownSelection();
						return null;
					}
				}

				return storageData;
			} catch (e) {
				console.warn('Company Type Dropdown: Could not read selection from localStorage', e);
				return null;
			}
		},

		/**
		 * Clear the saved dropdown selection from localStorage
		 */
		clearDropdownSelection: function() {
			try {
				localStorage.removeItem('arival_company_type_dropdown_selection');
			} catch (e) {
				console.warn('Company Type Dropdown: Could not clear selection from localStorage', e);
			}
			// Also clear the operator type cookie
			this.setCookie('arival_operator_type', '', -1);
		}
	};

	// Initialize on page load
	CompanyTypeDropdown.init();

	// Re-initialize on dynamic content load (for Bricks Builder)
	if (typeof window.MutationObserver !== 'undefined') {
		let observerTimeout = null;
		
		const observer = new MutationObserver(() => {
			// Debounce: Only check for new dropdowns if we haven't checked recently
			if (observerTimeout) {
				clearTimeout(observerTimeout);
			}
			
			observerTimeout = setTimeout(() => {
				const dropdowns = document.querySelectorAll('.company-type-dropdown.select2-select:not(.select2-hidden-accessible)');
				if (dropdowns.length > 0) {
					CompanyTypeDropdown.setupDropdowns();
				}
			}, 100); // Debounce by 100ms
		});

		// Start observing when body is available
		const startObserver = () => {
			if (document.body) {
				observer.observe(document.body, {
					childList: true,
					subtree: true
				});
				
				// Auto-disconnect after 10 seconds (or when all dropdowns are initialized)
				setTimeout(() => {
					observer.disconnect();
					if (observerTimeout) {
						clearTimeout(observerTimeout);
					}
				}, 10000);
			}
		};

		if (document.body) {
			startObserver();
		} else {
			document.addEventListener('DOMContentLoaded', startObserver);
		}
	}

	// Expose to global scope for manual control if needed
	window.ArivalCompanyTypeDropdown = CompanyTypeDropdown;

})();

